import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  formatDisplayText,
  formatValueSafely,
} from '@shared-lib/utils/text-case.utils';

type ToggleGroup = {
  title: string;
  key?: string;
  options: string[];
  selectionMode?: 'single' | 'multiple';
};

@Component({
  selector: 'multiselect-toggle',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './multiselect-toggle.html',
  styleUrl: './multiselect-toggle.css',
})
export class MultiselectToggle implements OnChanges {
  @Input() data?: {
    groups?: ToggleGroup[];
    selectionMode?: 'single' | 'multiple';
    selected?: Record<string, string[]>;
  };
  @Output() selectionChange = new EventEmitter<Record<string, string[]>>();

  groups: ToggleGroup[] = [];
  selectedByGroup: Record<string, string[]> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.groups = Array.isArray(this.data?.groups) ? this.data!.groups : [];
      this.selectedByGroup = { ...(this.data?.selected || {}) };
    }
  }

  toggleOption(group: ToggleGroup, option: string): void {
    const key = this.resolveGroupKey(group);
    const current = new Set(this.selectedByGroup[key] || []);
    const mode = group.selectionMode || this.data?.selectionMode || 'multiple';

    if (mode === 'single') {
      this.selectedByGroup[key] = current.has(option) ? [] : [option];
      this.selectionChange.emit({ ...this.selectedByGroup });
      return;
    }

    if (current.has(option)) {
      current.delete(option);
    } else {
      current.add(option);
    }

    this.selectedByGroup[key] = Array.from(current);
    this.selectionChange.emit({ ...this.selectedByGroup });
  }

  isSelected(group: ToggleGroup, option: string): boolean {
    return (this.selectedByGroup[this.resolveGroupKey(group)] || []).includes(option);
  }

  formatLabel(value: unknown): string {
    const mode = (this.data as any)?.textCase || 'capital';
    return formatDisplayText(value, mode);
  }

  formatOption(value: unknown): string {
    const mode = (this.data as any)?.textCase || 'capital';
    return formatValueSafely(value, mode);
  }

  private resolveGroupKey(group: ToggleGroup): string {
    return group.key || group.title.toLowerCase().replace(/\s+/g, '_');
  }
}
